import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { getNegotiation, sendNegotiationTurn, generateReport, createEmailDraft } from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Skeleton } from '../components/ui/skeleton';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { TacticsPanel, StrategyPanel, MemoryPanel } from '../components/adam/InsightsPanels';
import { toast } from 'sonner';
import { AdamStateMeter } from '../components/adam/AdamStateMeter';
import { CounterpartyProfile } from '../components/adam/CounterpartyProfile';
import {
  ArrowLeft, Send, FileText, Mail, AlertTriangle,
  Brain, User, Info, Loader2
} from 'lucide-react';

export default function NegotiationChat({ lang }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [negotiation, setNegotiation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailPurpose, setEmailPurpose] = useState('');
  const [generatingEmail, setGeneratingEmail] = useState(false);
  const [lastTurnStrategy, setLastTurnStrategy] = useState(null);
  const [lastTurnBluff, setLastTurnBluff] = useState(null);
  const [lastPipelineMeta, setLastPipelineMeta] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { loadNegotiation(); }, [id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [negotiation?.messages]);

  async function loadNegotiation() {
    setLoading(true);
    try {
      const data = await getNegotiation(id);
      setNegotiation(data);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleSend() {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      const result = await sendNegotiationTurn(id, { counterparty_message: message });
      setMessage('');
      // Capture enhanced pipeline data from response
      if (result.strategy) setLastTurnStrategy(result.strategy);
      if (result.tactics?.bluff_assessment) setLastTurnBluff(result.tactics.bluff_assessment);
      if (result.pipeline_meta) setLastPipelineMeta(result.pipeline_meta);
      // Reload full negotiation to get updated state
      await loadNegotiation();
    } catch (err) {
      console.error(err);
      toast.error(lang === 'pl' ? 'B\u0142\u0105d wysy\u0142ania' : 'Send error');
    }
    setSending(false);
  }

  async function handleGenerateReport() {
    setGeneratingReport(true);
    try {
      const report = await generateReport(id);
      toast.success(lang === 'pl' ? 'Raport wygenerowany' : 'Report generated');
      navigate(`/reports`);
    } catch (err) {
      toast.error(lang === 'pl' ? 'B\u0142\u0105d generowania raportu' : 'Report generation error');
    }
    setGeneratingReport(false);
  }

  async function handleGenerateEmail() {
    if (!emailPurpose.trim()) return;
    setGeneratingEmail(true);
    try {
      await createEmailDraft({ negotiation_id: id, purpose: emailPurpose });
      toast.success(lang === 'pl' ? 'Email wygenerowany' : 'Email generated');
      setEmailDialogOpen(false);
      setEmailPurpose('');
      navigate('/emails');
    } catch (err) {
      toast.error(lang === 'pl' ? 'B\u0142\u0105d' : 'Error');
    }
    setGeneratingEmail(false);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (loading) {
    return (
      <div className="h-[calc(100vh-80px)] flex gap-4">
        <Skeleton className="w-72 h-full" />
        <Skeleton className="flex-1 h-full" />
        <Skeleton className="w-80 h-full" />
      </div>
    );
  }

  if (!negotiation) return <p>Not found</p>;

  const messages = negotiation.messages || [];
  const contractor = negotiation.contractor;
  const profile = negotiation.current_profile;
  const state = negotiation.current_state;

  return (
    <div className="h-[calc(100vh-80px)] flex gap-3" data-testid="negotiation-chat-page">
      {/* Left Panel - Context */}
      <div className="w-72 flex-shrink-0 flex flex-col gap-3 overflow-y-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate('/negotiations')} className="self-start gap-1" data-testid="back-to-negotiations">
          <ArrowLeft className="w-4 h-4" />
          {t('back', lang)}
        </Button>

        <Card className="border border-border">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground">{lang === 'pl' ? 'Kontrahent' : 'Contractor'}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {contractor && (
              <div>
                <p className="text-sm font-medium">{contractor.name}</p>
                {contractor.company && <p className="text-xs text-muted-foreground">{contractor.company}</p>}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border border-border flex-1">
          <CardHeader className="pb-2">
            <CardTitle className="text-xs text-muted-foreground">{t('brief', lang)}</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <p className="text-sm leading-relaxed">{negotiation.brief}</p>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-2">
          <Button
            variant="secondary"
            size="sm"
            className="w-full gap-2 text-xs"
            onClick={handleGenerateReport}
            disabled={generatingReport || messages.length === 0}
            data-testid="generate-report-button"
          >
            {generatingReport ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
            {t('generateReport', lang)}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            className="w-full gap-2 text-xs"
            onClick={() => setEmailDialogOpen(true)}
            disabled={messages.length === 0}
            data-testid="generate-email-button"
          >
            <Mail className="w-3 h-3" />
            {t('generateEmail', lang)}
          </Button>
        </div>
      </div>

      {/* Center Panel - Chat */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Chat Header */}
        <div className="h-12 flex items-center justify-between px-4 border-b border-border bg-card rounded-t-xl">
          <div className="flex items-center gap-2">
            <h2 className="text-sm font-semibold truncate" style={{ fontFamily: 'Space Grotesk' }}>
              {negotiation.title}
            </h2>
            <Badge variant="secondary" className="text-xs">
              {lang === 'pl' ? `Tura ${negotiation.turn_count || 0}` : `Turn ${negotiation.turn_count || 0}`}
            </Badge>
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 bg-background/50 border-x border-border" ref={scrollRef}>
          <div className="p-4 space-y-4 min-h-full">
            {messages.length === 0 && (
              <div className="text-center py-12">
                <Brain className="w-10 h-10 text-primary/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  {lang === 'pl' ? 'Rozpocznij negocjacj\u0119 - wpisz wiadomo\u015b\u0107 kontrahenta' : 'Start negotiation - type counterparty message'}
                </p>
              </div>
            )}

            {messages.map((msg, i) => (
              <div key={msg.id || i} className={`flex ${msg.role === 'counterparty' ? 'justify-start' : 'justify-end'}`}>
                <div className={`max-w-[75%] space-y-1 ${
                  msg.role === 'counterparty'
                    ? 'bg-secondary border border-border rounded-2xl px-3 py-2'
                    : 'bg-white/5 border border-border rounded-2xl px-3 py-2'
                }`}>
                  {/* Role label */}
                  <div className="flex items-center gap-1.5 mb-1">
                    {msg.role === 'counterparty' ? (
                      <User className="w-3 h-3 text-muted-foreground" />
                    ) : (
                      <Brain className="w-3 h-3 text-primary" />
                    )}
                    <span className="text-xs font-medium text-muted-foreground">
                      {msg.role === 'counterparty'
                        ? (contractor?.name || 'Kontrahent')
                        : (negotiation.agent_name || 'Adam')}
                    </span>
                    <span className="text-xs text-muted-foreground/50 font-mono">
                      {formatDate(msg.timestamp)}
                    </span>
                  </div>

                  {/* Message content */}
                  <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>

                  {/* Warnings */}
                  {msg.warnings && msg.warnings.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {msg.warnings.map((w, wi) => (
                        <Badge key={wi} variant="outline" className="text-xs border-amber-400/30 text-amber-400 gap-1">
                          <AlertTriangle className="w-2.5 h-2.5" /> {w}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Supervisor Note */}
                  {msg.supervisor_note && (
                    <div className="mt-2 p-2 rounded-lg bg-[rgba(216,176,122,0.10)] border border-[rgba(216,176,122,0.22)]">
                      <div className="flex items-center gap-1 mb-0.5">
                        <Info className="w-3 h-3 text-accent" />
                        <span className="text-xs font-medium text-accent">{t('supervisorNote', lang)}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{msg.supervisor_note}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {sending && (
              <div className="flex justify-end">
                <div className="bg-white/5 border border-border rounded-2xl px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">
                      {lang === 'pl' ? 'Adam analizuje taktyki, planuje strategię i formułuje odpowiedź...' : 'Adam is analyzing tactics, planning strategy, and crafting response...'}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Composer */}
        <div className="p-3 border border-border bg-card rounded-b-xl" data-testid="chat-message-composer">
          <div className="flex gap-2">
            <Textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('typeMessage', lang)}
              className="min-h-[44px] max-h-[120px] resize-none text-sm"
              disabled={sending}
              data-testid="chat-message-input"
            />
            <Button
              onClick={handleSend}
              disabled={!message.trim() || sending}
              className="px-4 self-end"
              data-testid="chat-send-button"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>

      {/* Right Panel - Enhanced Insights */}
      <div className="w-80 flex-shrink-0 overflow-y-auto space-y-3">
        <Tabs defaultValue="profile">
          <TabsList className="w-full bg-muted/50 grid grid-cols-4">
            <TabsTrigger value="profile" className="text-xs px-1">
              {lang === 'pl' ? 'Profil' : 'Profile'}
            </TabsTrigger>
            <TabsTrigger value="tactics" className="text-xs px-1">
              {lang === 'pl' ? 'Taktyki' : 'Tactics'}
            </TabsTrigger>
            <TabsTrigger value="strategy" className="text-xs px-1">
              {lang === 'pl' ? 'Strategia' : 'Strategy'}
            </TabsTrigger>
            <TabsTrigger value="adam" className="text-xs px-1">
              Adam
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-3">
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground">{t('psychProfile', lang)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <CounterpartyProfile profile={profile} lang={lang} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tactics" className="mt-3">
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground">
                  {lang === 'pl' ? 'Wykrywanie taktyk' : 'Tactic Detection'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <TacticsPanel
                  tactics={{
                    detected: negotiation.detected_tactics || [],
                    bluff_assessment: lastTurnBluff || {},
                    negotiation_phase: negotiation.negotiation_phase || 'opening',
                    counterparty_style: profile?.negotiation_style || 'unknown'
                  }}
                  lang={lang}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="strategy" className="mt-3">
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground">
                  {lang === 'pl' ? 'Strategia & ZOPA' : 'Strategy & ZOPA'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <StrategyPanel
                  strategy={lastTurnStrategy}
                  zopa={negotiation.zopa_estimate}
                  lang={lang}
                />
              </CardContent>
            </Card>

            {/* Memory Panel */}
            <Card className="border border-border mt-3">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground">
                  {lang === 'pl' ? 'Inteligencja negocjacyjna' : 'Negotiation Intelligence'}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <MemoryPanel
                  memory={{
                    key_facts: negotiation.key_facts || [],
                    leverage_points: negotiation.leverage_points || [],
                    contradictions: negotiation.contradictions || [],
                    promises: negotiation.promises || {},
                    concession_tracker: negotiation.concession_tracker || {}
                  }}
                  lang={lang}
                />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="adam" className="mt-3">
            <Card className="border border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-xs text-muted-foreground">{t('internalState', lang)}</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <AdamStateMeter state={state} lang={lang} />
              </CardContent>
            </Card>

            {/* Quality Score */}
            {negotiation.quality_score > 0 && (
              <Card className="border border-border mt-3">
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {lang === 'pl' ? 'Jakość odpowiedzi' : 'Response Quality'}
                    </span>
                    <span className={`text-lg font-semibold tabular-nums ${
                      negotiation.quality_score >= 8 ? 'text-emerald-400' :
                      negotiation.quality_score >= 6 ? 'text-amber-400' : 'text-red-400'
                    }`} style={{ fontFamily: 'Space Grotesk' }}>
                      {negotiation.quality_score}/10
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Pipeline Timing (if available from last turn) */}
            {lastPipelineMeta && (
              <Card className="border border-border mt-3">
                <CardHeader className="pb-2">
                  <CardTitle className="text-xs text-muted-foreground font-mono">Pipeline</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="space-y-1">
                    {lastPipelineMeta.phases?.map((phase, i) => (
                      <div key={i} className="flex items-center justify-between text-xs">
                        <span className={`${phase.success ? 'text-muted-foreground' : 'text-red-400'}`}>
                          {phase.phase}
                        </span>
                        <span className="font-mono text-muted-foreground/60">{phase.duration_ms}ms</span>
                      </div>
                    ))}
                    <Separator className="my-1" />
                    <div className="flex items-center justify-between text-xs font-medium">
                      <span>Total</span>
                      <span className="font-mono">{lastPipelineMeta.total_time_ms}ms</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Email Draft Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('generateEmail', lang)}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-muted-foreground">{t('purpose', lang)}</label>
              <Input
                value={emailPurpose}
                onChange={e => setEmailPurpose(e.target.value)}
                placeholder={lang === 'pl' ? 'Np. Potwierdzi\u0107 warunki dostawy...' : 'e.g. Confirm delivery terms...'}
                className="mt-1"
              />
            </div>
            <Button onClick={handleGenerateEmail} disabled={generatingEmail || !emailPurpose.trim()} className="w-full">
              {generatingEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {t('generateEmail', lang)}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
